import type { IEventChannel } from "./model/Models";

const $: JQueryStatic = global.$;

const es = global.es;

/**
 * Generate a request ID for log correlation across frontend and backend.
 * Format: [uuid first 8 chars]-[tab slot]-[timestamp last 6 chars]-[random]
 * e.g., "550e8400-1-234567-42" or "550e8400-pageload-234567-42"
 *
 * - uuid first 8: identifies the browser instance (from crypto.randomUUID())
 * - tab slot: tab number (1, 2, 3...) or "pageload" during initial page load
 * - timestamp last 6: i.e. about 15min window
 * - random: 2-digit random (10-99) for extra collision avoidance
 *
 * Used by AjaxRequestBroker for API calls and transformRequest for map tile requests.
 */
export function generateRequestId(): string {
    if (es?._browserTabId) {
        // browserTabId format: "es_tab-550e8400-e29b-41d4-a716-446655440000_1"
        const uuidFirst8 = es._browserTabId.substring(7, 15);
        const tabSlot = es._browserTabId.substring(es._browserTabId.lastIndexOf('_') + 1);
        const timestampLast6 = String(Date.now()).slice(-6);
        const rnd = Math.floor(Math.random() * 90) + 10;
        return `${uuidFirst8}-${tabSlot}-${timestampLast6}-${rnd}`;
    }
    // Fallback if no browserTabId available
    return `${Date.now()}-${Math.floor(Math.random() * 90) + 10}`;
}

/**
 * @class AjaxRequestBroker
 */
export default class AjaxRequestBroker {
    readonly MAX_URL_LENGTH = 2000;

    private eventChannel: IEventChannel | null;
    private loadJobDetails: any[];
    // Basic auth header to be used in all api calls. see also: esMapping/es/api.js
    private _auth: object | null;


    // Setting this to true will mean that parallel GET requests to the same resource will result in
    // just one server request and when it has completed it will resolve all requests with the same response
    private allowIdenticalParallelGetRequests: boolean = false;
    private deferredQueue: Array<{url: string, dfd: JQueryDeferred<any>}> = [];

    /**
     * Simple counter mechanism to keep track of parallel hits to various urls
     * see: es.api.getRequestBroker()._identicalParallelGetRequestStats.stats()
     */
    private _identicalParallelGetRequestStats = new es.util.Counter();

    /**
     * 
     * @param eventChannel 
     */
    constructor(eventChannel: IEventChannel) {
        this.eventChannel = eventChannel;
        this.loadJobDetails = [];
        this._auth = null;

        // TODO FIXME Does this even make sense nowadays?
        eventChannel.addListener("onDestroy", () => {
            //es.log.info("ajaxrqbroker onDestroy");

            // prevent memory leaks when the map widget is destroyed
            this.eventChannel = null;
        });

        this.setAuth = this.setAuth.bind(this);
        this.doPostPromise = this.doPostPromise.bind(this);
        this.doPost = this.doPost.bind(this);
        this.doPutPromise = this.doPutPromise.bind(this);
        this.doPut = this.doPut.bind(this);
        this.doGetPromise = this.doGetPromise.bind(this);
        this.doGet = this.doGet.bind(this);
        this.doDeletePromise = this.doDeletePromise.bind(this);
        this.doDelete = this.doDelete.bind(this);
        this.executeRQ = this.executeRQ.bind(this);
        this.processDfdQueue = this.processDfdQueue.bind(this);
        this.resetAutomaticSessionTimeoutRedirect = this.resetAutomaticSessionTimeoutRedirect.bind(this);
        this.loadStarted = this.loadStarted.bind(this);
        this.loadFinished = this.loadFinished.bind(this);

        // init timeout redirect
        this.resetAutomaticSessionTimeoutRedirect();

        // Generate unique browser tab ID for this page load
        if (!es["_browserTabId"]) {
            this.initBrowserTabId();
        }
    }

    /**
     * 
     * @param auth 
     */
    setAuth(auth) {
        if(auth) {
            this._auth = {"Authorization": "Basic " + btoa(auth.basicAuth.email + ":" + auth.basicAuth.password)};
        } else {
            this._auth = null;
        }
    }

    /**
     * Temporary wrapper method for TOR9-19295
     * 
     * @param request { url: url, data: metaObj, opts: opts }
     * 
     * @returns
     */
    doPostPromise(request): Promise<any> {
        return new Promise((resolve, reject) => {
            this.doPost(request)
                .done(resolve)
                .fail(reject);
        });
    }

    /**
     * execute a POST ajax request
     * 
     * @param params { url: url, data: metaObj, opts: opts }
     * 
     * @returns a deferred object resolved when request finishes
     */
    doPost(params): JQueryDeferred<any> {
        const mandatoryParams = {
            type: "POST",
            contentType : params.contentType ? params.contentType : 'application/json',
            dataType : params.dataType ? params.dataType : 'json'
        };

        if (params.data && !params.contentType) {
            if (params.opts && params.opts.doNotJsonify === true) {
                //skip
            } else {
                mandatoryParams.data = JSON.stringify(params.data);
            }
        }

        params = $.extend(true, params, mandatoryParams);

        return this.executeRQ(params);
    }

    /**
     * Temporary wrapper method for TOR9-19295
     * 
     * @param params { url: url, data: metaObj, opts: opts }
     * 
     * @returns
     */
    doPutPromise(params): Promise<any> {
        return new Promise((resolve, reject) => {
            this.doPut(params)
                .done(resolve)
                .fail(reject);
        });
    }

    /**
     * execute a PUT ajax request
     * 
     * @param params { url: url, data: metaObj, opts: opts }
     * 
     * @returns a deferred object resolved when request finishes
     */
    doPut(params): JQueryDeferred<any> {
        const mandatoryParams = {
            type: "PUT",
            contentType : 'application/json',
            dataType : params.dataType ? params.dataType : 'json',
            data: JSON.stringify( params.data || null ) // TOR9-11288 - Safari adds weird header to PUT requests with empty body. Sending null instead...
        };

        params = $.extend(true, params, mandatoryParams);

        return this.executeRQ(params);
    }

    /**
     * Temporary wrapper method for TOR9-19295
     * 
     * @param request { url: url, data: metaObj, opts: opts }
     * 
     * @returns
     */
    doGetPromise(request): Promise<any> {
        return new Promise((resolve, reject) => {
            this.doGet(request)
                .done(resolve)
                .fail(reject);
        });
    }

    /**
     * execute a GET ajax request
     * 
     * @param params { url: url, data: metaObj, opts: opts }
     * 
     * @return a deferred object resolved when request finishes
     */
    doGet(params): JQueryDeferred<any> {
        const mandatoryParams = {
            type: "GET",
            contentType : 'application/json',
            dataType : params.dataType ? params.dataType : 'json'
        };
        if (params.data) {
            mandatoryParams.data = params.data ;
        }

        if (params.url.length > this.MAX_URL_LENGTH) {
            console.error("This GET request might fail as URL is long: " + params.url.length + ". This warn msg logged when url longer than " + this.MAX_URL_LENGTH, params);
            if(es["isDeveloper"]){
                debugger;
            }
        }

        // ie likes to cache things even if you don't ask it to (TOR9-120)
        if (es.util.browser.isIE() || (params.opts && params.opts.noCache)) {
            mandatoryParams.cache = false;
        }

        params = $.extend(true, params, mandatoryParams);

        return this.executeRQ(params);
    }

    /**
     * Temporary wrapper method for TOR9-19295
     * 
     * @param params { url: url, data: metaObj, opts: opts }
     * 
     * @returns
     */
    doDeletePromise(request): Promise<any> {
        return new Promise((resolve, reject) => {
            this.doDelete(request)
                .done(resolve)
                .fail(reject);
        });
    }

    /**
     * execute a DELETE ajax request
     * 
     * @param params { url: url, data: metaObj, opts: opts }
     * 
     * @returns a deferred object resolved when request finishes
     */
    doDelete(params): JQueryDeferred<any> {
        const mandatoryParams = {
            type: "DELETE",
            contentType : 'application/json',
            dataType : params.dataType ? params.dataType : 'json',
            data: JSON.stringify( params.data )
        };

        params = $.extend(true, params, mandatoryParams);

        return this.executeRQ(params);
    };

    /**
     * execute an ajax request
     * 
     * @param params { url: url, data: metaObj, opts: opts }
     * 
     * @returns a deferred object resolved when request finishes
     */
    private executeRQ(params): JQueryDeferred<any> {
        /*
        if (_this == null && es.isDeveloper) {
            es.log.error('Using destroyed ajax request broker... unless it\'s onBeforeUnload, there\'s something wrong!!!', params);
        }
        */

        params = es.util.object.deepCopy(params);

        if (es.isPublicAccess == true) {
            params.url = params.url + (params.url.indexOf('?')==-1 ? "?" : "&");
            params.url = params.url + "TENANT_ID=" + es.tenantId;
        }

        //es.log.info("deferredQueue,length", deferredQueue.length);

        const url = params.url;
        if (!url) {
            throw new Error("executeRQ params.url cannot be null");
        }

        if (params.opts && params.opts.basicAuth) {
            params.headers = {"Authorization": "Basic " + btoa(params.opts.basicAuth.email + ":" + params.opts.basicAuth.password)};
            delete params.opts.basicAuth;
            this.allowIdenticalParallelGetRequests = false; // if we allow this then a different user who hits the same url that another user has just hit and is awaiting a response could each get the same response
            //es.log.info("using basic auth", params.headers);
        } else if (this._auth) {
            params.headers = this._auth;
        }

        params.beforeSend = () => this.loadStarted(url);
        params.complete = () => this.loadFinished(url);

        const deferred = $.Deferred();
        const isGet = (params.type.toUpperCase() == "GET");
        if (isGet && !this.allowIdenticalParallelGetRequests) {
            const i = this.deferredQueue.findIndex(dq => dq.url == url);
            if (i > -1) {
                if (es.isDeveloper) {
                    this._identicalParallelGetRequestStats.add(url);
                }
                //deferredQueue[i].count++;
                //const stack = new Error("non-error-just-stack").stack;
                //if(deferredQueue[i].stack.indexOfObjWith("stack", stack)){
                //    console.warn("Concurrent requests for resource with same stack (possible event mishandling/over-subscription)", url);
                //}
                //deferredQueue[i].stack.push(stack);

                return this.deferredQueue[i].dfd;
            }
            //es.log.info("Add to dfd queue" + url);
            this.deferredQueue.push({url: url, dfd: deferred/*, count: 1, stack: [new Error("non-error-just-stack").stack]*/});
        }

        this.loadStarted(url);

        this.resetAutomaticSessionTimeoutRedirect();

        const resolveWithXHR = params.opts && params.opts['resolveWithXHR'];

        if (!es.isPublicAccess) {
            if (!params.headers) {
                params.headers = {};
            }

            params.headers["X-Embedded-In-SFDC"] = es.isEmbeddedInSFDC;
            params.headers["X-ES-User"] = es.tenantId + "_" + es.userName;

            if(es?._browserTabId){
                params.headers["X-BrowserTab-Id"] = es._browserTabId;
            }

            params.headers["X-Request-Id"] = generateRequestId();
            //es.log.log("set X-Request-Id", params.headers["X-Request-Id"]);
        }

        //params.stack = new Error(url).stack

        $.ajax(params).done((rs: any, status, jqXHR: JQueryXHR) => {
            this.loadFinished(url);

            if (isGet && !this.allowIdenticalParallelGetRequests) {
                if (resolveWithXHR){
                    this.processDfdQueue(url, "resolve", rs, jqXHR);
                } else {
                    this.processDfdQueue(url, "resolve", rs);
                }
            } else {
                if (resolveWithXHR) {
                    deferred.resolve(rs, jqXHR);
                } else {
                    deferred.resolve(rs);
                }
            }

        }).fail((jqXHR: JQueryXHR, textStatus: string) => {
            const handleRequestFail = (jqXHR: JQueryXHR, textStatus: string, params: object, url: string, deferred: JQueryDeferred) => {
                function isToHandleForbiddenAsStale() {
                    return (es.isPublicAccess ||
                        window.location.href.indexOf("map.jsp") > -1 ||
                        window.location.href.indexOf("directions.jsp") > -1 ||
                        window.location.href.indexOf("scheduler.jsp") > -1 ||
                        window.location.href.indexOf("locations.jsp") > -1 ||
                        window.location.href.indexOf("style.jsp") > -1 ||
                        window.location.href.indexOf("territories.jsp") > -1);
                }

                const handleStaleResource = () => {
                    if( es.isPublicAccess ||
                        window.location.href.indexOf("map.jsp") > -1 ||
                        window.location.href.indexOf("directions.jsp") > -1 ||
                        window.location.href.indexOf("scheduler.jsp") > -1 ||
                        window.location.href.indexOf("locations.jsp") > -1 ||
                        window.location.href.indexOf("style.jsp") > -1 ||
                        window.location.href.indexOf("territories.jsp") > -1 ) {

                        const key = "es_stale_resource_" + window.location.href;
                        if ($.sessionStorage.getItem(key) == "true" && !es.isPublicAccess) {
                            // same error twice... redirect to library page
                            $.sessionStorage.removeItem(key);
                            window.location.href = window["esMobile"] ? "map.jsp" : "home.jsp"; //NOSONAR
                        } else {
                            $.sessionStorage.setItem(key, "true");
                            this.eventChannel.fireEvent("onStaleResource", key);
                        }
                    }
                }

                const handleDeferreds = () => {
                    if (isGet && !this.allowIdenticalParallelGetRequests) {
                        this.processDfdQueue(url, "reject", null, jqXHR);
                    } else {
                        deferred.reject(jqXHR, textStatus);
                    }
                }

                if (es["suppressDefaultErrorHandling"] == true || params['opts'] && params.opts['suppressDefaultErrorHandling']) {
                    // Handle case where app programmer doesn't want us to handle the error
                    // e.g.:
                    //    es.api.geoMap.get(-1, {suppressDefaultErrorHandling: true}).done(function(){
                    //        es.log.info("DONE");
                    //    }).fail(function(){
                    //        es.log.info("YES, I KNOW THIS WILL FAIL BUT I'LL HANDLE IT MYSELF");
                    //    });
                    if (isGet && !this.allowIdenticalParallelGetRequests) {
                        this.processDfdQueue(url, "reject", null, jqXHR);
                    } else {
                        deferred.reject(jqXHR);
                    }
                    return;
                }

                this.eventChannel.fireEvent("onExecuteRqFail", jqXHR);

                if (jqXHR["responseJSON"]) {
                    const rs = jqXHR["responseJSON"];
                    if (rs.isServerError || rs.isServerError == "true") {
                        if (rs.exceptionMessage &&
                            (rs.exceptionMessage.indexOf("LayerView does not exist in GeoMap") > -1 ||
                                rs.exceptionMessage.indexOf("User is not entitled to get GeoMap") > -1 ||
                                rs.exceptionMessage.indexOf("User is not entitled to get LayerView") > -1 ||
                                rs.exceptionMessage.indexOf("Invalid Layer view parameters") > -1 ||
                                (rs.exceptionMessage.indexOf("You are forbidden from accessing this resource") > -1 && isToHandleForbiddenAsStale()))) {

                            handleStaleResource();
                        } else {
                            let errorMsg;
                            if (rs.code) {
                                errorMsg = $i18n(rs.code, undefined, true);
                                if (errorMsg.endsWith('[I18N ERR]')) {
                                    errorMsg = undefined;
                                }
                            }

                            errorMsg = errorMsg || rs.exceptionMessage || "An unexpected error occurred while server was processing your request";
                            es.error.handleJSError(errorMsg);
                        }
                    } else {
                        // TODO should we do if(es["suppressDefaultErrorHandling"] == true || params['opts'] && params.opts['suppressDefaultErrorHandling']){
                        // what should it do if error but no error code?
                        deferred.reject(jqXHR);
                    }
                } else {
                    // TODO: need to sort out this error handling. need well defined exception for this type instead of text
                    if (jqXHR.status === 404 && jqXHR.responseText.indexOf('Tenant not found') > -1) {
                        if (es.isPublicAccess) {
                            // Handle bookmarks that are no longer public . redirect to 404 and include referral info (or can get this wordpress with php $_SERVER['HTTP_REFERER'])
                            location.href = es.publicNotSharedURL + "?ref=" + encodeURI(params.url);
                        } else {
                            // TODO: throw an event like es.api.getEventChannel().NOT_FOUND
                            if (window["esApp"]) {
                                location.href = esApp.util.getUrl("login/404-error", true) + "?ref=" + encodeURI(params.url);
                            } else if(window["esMobile"]) {
                                esMobile.util.navigateTo("map.jsp#/OpenMap/true");
                            }
                        }
                    } else if( jqXHR.readyState === 0 && jqXHR.status === 0) {
                        // Connection to the server is lost (most likely). Redirecting to login page will also fail but will at least remove the app UI from screen
                        // see TOR9-768: This can also happen in Firefox if user clicks a button to redirect to another page while the current page is not fully loaded
                        //location.href = es.appUrl;
                    } else if( jqXHR.status === 401 && jqXHR.responseText.includes("Error authenticating with Salesforce.com") ) {
                        es.error.handleSfdcServerErrorWithXHR(jqXHR);
                        
                    } else if (jqXHR.status === 403 && (jqXHR.responseText.indexOf('User is not entitled to get GeoMap') > -1 || jqXHR.responseText.indexOf('Not Authorized to perform action [GEOMAP_GET]') > -1)){
                        const key = "es_forbidden_" + window.location.href;
                        if ($.sessionStorage.getItem(key) === "true" && !es.isPublicAccess) {
                            // same error twice... redirect to library page
                            $.sessionStorage.removeItem(key);
                            window.location.href = window["esMobile"] ? "map.jsp" : "home.jsp"; //NOSONAR
                            return;
                        }
                        const reg = /\d+/;
                        const geoMapId = jqXHR.responseText.match(reg)[0] ?? -1;
                        es.error.handleServerError("forbidden", "Could not access Map with id: " + geoMapId, "403");
                        $.sessionStorage.setItem(key, "true");
                    } else {
                        if(
                            jqXHR.responseText.indexOf("LayerView does not exist in GeoMap") > -1 ||
                            jqXHR.responseText.indexOf("User is not entitled to get GeoMap") > -1 ||
                            jqXHR.responseText.indexOf("User is not entitled to get LayerView") > -1 ||
                            (jqXHR.responseText.indexOf("You are forbidden from accessing this resource") > -1
                                && jqXHR.responseText.indexOf("you must belong to a group with permission to") == -1
                                && isToHandleForbiddenAsStale())) {
                            handleStaleResource();
                        } else {
                            es.error.handleServerErrorWithXHR(jqXHR);
                        }
                    }
                }
                handleDeferreds();
            }

            this.loadFinished(url);

            if ( (jqXHR?.responseText?.indexOf('_Login_Title') > -1 && jqXHR.responseText.indexOf('j_security_check') > -1) ||
                 (jqXHR?.responseText?.indexOf('You must be authenticated to access this resource [Session timeout? Security Context Not Found.]') > -1)
            ) {
                this.eventChannel.fireEvent("onSessionTimeout", "ajax_broker_got_login_rs");
            } else if (jqXHR.status >= 400 && jqXHR.status < 500 ){
                //ajax 4xx errors means timeout and we should redirect in case sessionready is not available == session timeout
                //http 4xx errors should be treated as ordinary errors in case sessionready returns http 200
                const sessionReadyUrl = (es.isPublicAccess ? es.url : es.appUrl) + 'sessionready';

                $.ajax({url: sessionReadyUrl, dataType: 'json'}).always((sessionReadyRS, success, sessionReadyStatusObj) => {
                    const isLoginPage = sessionReadyStatusObj.responseText && sessionReadyStatusObj.responseText.indexOf('_Login_Title') > -1
                        && sessionReadyStatusObj.responseText.indexOf('j_security_check') > -1;

                    if (sessionReadyStatusObj.status !== 200 || isLoginPage) {
                        this.eventChannel.fireEvent("onSessionTimeout", "ajax_broker_got_login_rs");
                    } else {
                        handleRequestFail(jqXHR, textStatus, params, url, deferred);
                    }
                });
            } else if (jqXHR.status == 200 && jqXHR.getResponseHeader("es_remember_me_recovery") == "1"){
                // We assume the session has been timed out but refreshed by remember-me functionality
                es.log.warn("Session timeout and recovery via remember-me detected");
                this.eventChannel.fireEvent("onRememberMeSessionRecovery");
            } else {
                handleRequestFail(jqXHR, textStatus, params, url, deferred);
            }
        });

        return deferred.promise();
    };

    /**
     * Process the queue of deferred objects, assigning the same outcome (resolve or reject)
     * to each one with a matching url and then removes it from the queue.
     */
    private processDfdQueue(url: string, outcome: string, rs: any, jqXHR?: JQueryXHR) {
        for(let i = this.deferredQueue.length - 1; i > -1; i--) {
            if (this.deferredQueue[i].url == url) {
                ((dfd, url, /*count,*/ status) => {
                    window.setTimeout(() => {
                        if (status == "resolve") {
                            // if (count > 1 && es.isDeveloper) {
                            //     console.info("processDfdQueue resolving " + count + " promises for", url, es.util.getFileSize(JSON.stringify(rs).length)); // stringify action takes time here but is only used in dev mode
                            // }
                            if (jqXHR) {
                                //dfd.resolve(count > 1 ? es.util.object.deepCopy(rs) : rs, jqXHR);
                                dfd.resolve(rs, jqXHR);
                            } else {
                                //dfd.resolve(count > 1 ? es.util.object.deepCopy(rs) : rs);
                                dfd.resolve(rs);
                            }
                        } else if(status == "reject") {
                            //dfd.reject(es.util.object.deepCopy(rs), jqXHR);
                            dfd.reject(rs, jqXHR);
                        }
                        for (let i = this.deferredQueue.length - 1; i > -1; i--) {
                            if (this.deferredQueue[i].url == url) {
                                //es.log.info("Removing", deferredQueue[i], "from queue ", deferredQueue);
                                this.deferredQueue.splice(i, 1);
                                break; // There can be only one!
                            }
                        }
                    }, 0);
                })(this.deferredQueue[i].dfd, this.deferredQueue[i].url, /*this.deferredQueue[i].count,*/ outcome);
                break;
            }
        }
    }

    /**
     * Fire an event when the session is about to timeout (timeout time is grabbed from global js var taken from request.getSession().getMaxInactiveInterval()))
     */
    private resetAutomaticSessionTimeoutRedirect() {
        if (!es.isPublicAccess) {
            if (es['sessionTimeoutID']) {
                //es.log.info("resetAutomaticSessionTimeoutRedirect : RESET");
                clearTimeout(es.sessionTimeoutID);
            }

            es.sessionTimeoutID = window.setTimeout(() => {
                this.eventChannel.fireEvent("onSessionTimeout", "timer_triggered_" + es.sessionTimeout);
            }, ((es.sessionTimeout * 1000) + 1000)); // set timer to be session.getMaxInactiveInterval plus 1 second

            if (!es.isEmbeddedInSFDC) {
                if (es['sessionTimeoutWarnID']) {
                    clearTimeout(es.sessionTimeoutWarnID);
                }

                // set timer to be session.getMaxInactiveInterval minus 60 seconds
                // if we are on the territory compare page, we set the warning timeout a bit shorter, so we don't ran into the issue
                // where the iframes had a session timeout and the main page still has a session.
                const timeout = (es.sessionTimeout * 1000) - (es.isTerritoryComparePage ? 55000 : 60000)
                es.sessionTimeoutWarnID = window.setTimeout(() => {
                    this.eventChannel.fireEvent("onSessionTimeoutWarn");
                }, timeout);
            }
            //es.log.info("resetAutomaticSessionTimeoutRedirect : SET");
        }
    }

    /**
     * function called when loading states
     * 
     * @param url
     */
    private loadStarted(url: string) {
        this.loadJobDetails.push(url);

        this.eventChannel.fireEvent("onLoadStart", url, this.loadJobDetails);
    }

    /**
     * function called when loading finishes
     * 
     * @param url
     */
    private loadFinished(url: string) {
        const index = this.loadJobDetails.indexOf(url);
        if (index >= 0) {
            this.loadJobDetails.splice(index, 1);
        }

        this.eventChannel.fireEvent("onLoadFinish", url, this.loadJobDetails);
    }

    /**
     * Initialize a unique browser tab id using BroadcastChannel for coordination.
     * Sets an immediate _init id, then coordinates with other tabs to find a unique slot number.
     */
    private initBrowserTabId() {
        let baseId: string;
        try {
            baseId = `es_tab-${crypto.randomUUID()}`;
        } catch(err) {
            baseId = `es_tab-${Date.now()}_${Math.random()}`;
            es.log.warn("browserTabId set (fallback)", es._browserTabId, err);
        }

        // set initial id immediately so requests can flow and be tracked. e.g. we'll know the err happened on page load (or shortly after)
        es._browserTabId = `${baseId}_pageload`;
        //es.log.log("browserTabId init as", es._browserTabId);

        // coordinate with any other tabs to find unique slot number
        try {
            const channel = new BroadcastChannel("es-tab-coord");
            const usedSlots = new Set<number>();
            let coordinationComplete = false;

            channel.onmessage = (e: MessageEvent) => {
                if (e.data.type === 'slot-taken') {
                    usedSlots.add(e.data.slot);
                } else if (e.data.type === 'slot-check') {
                    // another tab is checking - respond if they're trying to use our slot
                    const ourSlot = parseInt(es._browserTabId.split('_').pop() || '0');
                    const isDifferentTab = e.data.baseId !== baseId;
                    const isSameSlot = e.data.slot === ourSlot;

                    if (isDifferentTab && isSameSlot) {
                        channel.postMessage({type: 'slot-taken', slot: e.data.slot});
                    }
                }
            };

            // start with slot 1 and check if it's taken
            let slotNumber = 1;
            channel.postMessage({ type: 'slot-check', slot: slotNumber, baseId: baseId });

            // wait briefly for responses, then assign the final id
            setTimeout(() => {
                // find first available slot
                while (usedSlots.has(slotNumber)) {
                    slotNumber++;
                }

                es._browserTabId = `${baseId}_${slotNumber}`;
                coordinationComplete = true;
                es.log.log("browserTabId set", es._browserTabId);

                // then keep listening for other tabs coord rqs
                channel.onmessage = (e: MessageEvent) => {
                    const isDifferentTab = e.data.baseId !== baseId;
                    const isSameSlot = e.data.slot === slotNumber;

                    if (e.data.type === 'slot-check' && isDifferentTab && isSameSlot) {
                        channel.postMessage({type: 'slot-taken', slot: slotNumber});
                    }
                };
            }, 100);

        } catch(err) {
            es._browserTabId = `es_tab-${Date.now()}_${Math.random()}`;
            es.log.log("browserTabId set (fallback)", es._browserTabId, err);
        }
    }
}
